// Stage 3, live (spec §2). A full-bleed map under four floating panels: the report feed on the left, the
// selected unit, the advisors and the order deck on the right, the clock and the dials across the top,
// the command net meter and force status across the bottom. Real time; nothing pauses on its own.

import { ElicitCard } from '../elicit/ElicitCard';
import { currentContactEstimate, roleRingRadiusPx, type ContactEstimate } from '../../ai/contactEstimate';
import { getEstimator } from '../../ai/estimator';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type RefObject } from 'react';
import { MapView, type MapReady } from '../../map/MapView';
import { Overlay, type OverlayContact, type OverlayUnit } from '../../render/Overlay';
import { autoSlowSecondsLeft, useStore, SPEED_1_1, SPEED_10_1, SPEED_60_1, SPEED_240_1, type Speed } from '../../state/store';
import { card } from '../../sim/deck';
import { UNIT_DEFS } from '../../sim/scenario';
import type { Contact, Event, Graphic, Snapshot } from '../../sim/types';
import { TICKS_PER_MIN } from '../../sim/types';
import { Bar, Chip } from '../cards/ui';
import { UnitCard } from '../cards/UnitCard';
import { Advisors } from '../advisors/Advisors';
import { useAdvisors } from '../../state/advisors';
import { Deck } from '../deck/Deck';
import { NetMeter } from '../deck/NetMeter';
import { ReportFeed } from '../feed/ReportFeed';
import { useFeed } from '../../state/feed';
import { cardLabel, clockHM } from '../reports';
import { setPingHandler } from '../ping';
import { contactRingAlpha, contactSpreadM } from '../../cop/spread';
import { attachMapGestures } from '../../map/gestures';
import { Acronym, AcronymText } from '../Acronym';
import { Wordmark } from '../Wordmark';
import { CoachOverlay } from '../CoachOverlay';
import { RadialMenu } from '../radial/RadialMenu';
import { radialController } from '../radial/state';
import { DEBUG } from '../../debug';
import { rulesetLabel } from '../InstructorPanel';
// The wind tunnel's one line in this stage: what the plan now being fought measured at, before H-hour.
import { WindTunnelChip } from '../planning/WindTunnel';

const LEFT_RAIL = 320;
const RIGHT_RAIL = 360;
/** How close, in screen pixels, a click has to land to count as picking a control graphic or a contact. */
const HIT_PX = 24;
/** Backstop for letting the advisors start calling the model when the map never goes idle. */
const ADVISOR_RELEASE_MS = 4000;

/** How long a report's map ping stays up. */
const PING_MS = 1000;

/** How long a refused order's reason stays on the map, over the unit it was refused for. */
const REJECT_TOAST_MS = 6000;

/** Orders still on their way to a unit: the overlay draws a dashed pending ring around it. */
function pendingUnitIds(snap: Snapshot): Set<number> {
  const ids = new Set<number>();
  for (const o of snap.orders) {
    if (o.side !== 0) continue;
    if (o.status === 'queued' || o.status === 'transmitting' || o.status === 'received') ids.add(o.unitId);
  }
  return ids;
}

function contactLabel(c: Contact): string {
  if (c.typeConf === 'unknown') return 'UNK';
  if (c.typeConf === 'vehicle') return 'VEH';
  return UNIT_DEFS[c.typeConf].label.slice(0, 3).toUpperCase();
}

function distToSegment(px: number, py: number, a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - a.x, py - a.y);
  let t = ((px - a.x) * dx + (py - a.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (a.x + t * dx), py - (a.y + t * dy));
}

export function LiveStage() {
  const meta = useStore((s) => s.meta);
  const overlayRef = useRef<Overlay | null>(null);
  const pingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleReady = useCallback(({ map, container, projection }: MapReady) => {
    const overlay = new Overlay(container, map, projection);
    overlayRef.current = overlay;
    // The advisors hold a connection per model call; they wait until the map has its tiles, so
    // MapLibre gets the browser's six same-origin connections first (see src/state/advisors.ts
    // `mapReady`). The timer is the backstop: on a dead network `idle` may never come, and the rail
    // should still ask for plays rather than sit on templates forever.
    const releaseAdvisors = (): void => useAdvisors.getState().setMapReady(true);
    if (map.loaded()) releaseAdvisors();
    else map.once('idle', releaseAdvisors);
    const advisorRelease = setTimeout(releaseAdvisors, ADVISOR_RELEASE_MS);

    const toScreen = (p: { x: number; y: number }): { x: number; y: number } => {
      const [lng, lat] = projection.cmToLngLat(p.x, p.y);
      const s = map.project([lng, lat]);
      return { x: s.x, y: s.y };
    };

    /** Distance in screen pixels from a point to a control graphic's outline (or its centre). */
    const distToGraphic = (g: Graphic, px: number, py: number): number => {
      const pts = g.pts.map(toScreen);
      if (pts.length === 0) return Infinity;
      if (pts.length === 1) return Math.hypot(pts[0].x - px, pts[0].y - py);
      let best = Infinity;
      const closed = g.kind === 'obj' || g.kind === 'aa';
      for (let i = 0; i < pts.length; i++) {
        const next = pts[i + 1] ?? (closed ? pts[0] : null);
        best = Math.min(best, Math.hypot(pts[i].x - px, pts[i].y - py));
        if (next) best = Math.min(best, distToSegment(px, py, pts[i], next));
      }
      return best;
    };

    const scenario = useStore.getState().scenario;
    if (scenario) overlay.setGraphics(scenario.graphics);

    // ---- snapshot -> overlay, outside React ------------------------------------------------------
    let lastSnapshot: Snapshot | null = null;
    const contactRoles = new Map<number, ContactEstimate>();
    const readRoles = (): void => {
      contactRoles.clear();
      for (const enrichment of Object.values(useFeed.getState().enrichments)) {
        const role = enrichment.role;
        if (role && role.tick >= (contactRoles.get(role.enemyId)?.tick ?? -1)) contactRoles.set(role.enemyId, role);
      }
    };
    readRoles();
    const draw = (s = useStore.getState()): void => {
      const snap = s.snapshot;
      if (!snap) return;
      const pending = pendingUnitIds(snap);
      // Blue's own reported (rather than true) position, per unit, when C2 (command and control) is on:
      // a training aid, spec §7 "friendly positions lag by report latency". Off the net entirely (`c2 ===
      // 'off'`) blue units just show their true position, same as before this dial existed.
      const reportedMap = new Map(snap.picture.reported);
      const units: OverlayUnit[] = [];
      for (const u of snap.units) {
        // The common operating picture shows blue's own units and the contacts blue has reported.
        // Red's true positions stay hidden until the debrief (spec §7).
        if (u.side !== 0 || !u.alive) continue;
        const rep = s.dials.c2 !== 'off' ? reportedMap.get(u.id) : undefined;
        const useReported = rep !== undefined && rep.tick <= snap.tick;
        const x = useReported ? rep.x : u.x;
        const y = useReported ? rep.y : u.y;
        units.push({
          id: u.id, x, y,
          sidc: UNIT_DEFS[u.type].sidcBlue,
          side: 0,
          strength: u.strength,
          suppression: u.suppression,
          selected: u.id === s.selectedUnit,
          pending: pending.has(u.id),
          label: u.callsign,
          // Only drawn (as a dotted "report lag" line, a training aid) while this unit is selected, and
          // only when the reported position actually differs from the true one.
          truePos: useReported && (x !== u.x || y !== u.y) ? { x: u.x, y: u.y } : undefined,
        });
      }
      overlay.setUnits(units);
      const provider = getEstimator(s.dials).name;
      const contacts: OverlayContact[] = snap.picture.contacts.map((c) => {
        const role = currentContactEstimate(contactRoles.get(c.enemyId), c, snap.tick, provider);
        const ageMin = Math.max(0, (snap.tick - c.lastSeen) / TICKS_PER_MIN);
        // Older than 10 sim minutes: a faded "last seen" ghost rather than a live contact (spec §7).
        const ghost = ageMin >= 10;
        return {
          enemyId: c.enemyId, x: c.x, y: c.y, ageMin, ghost,
          // The ring is dead reckoning, not the original report's error bar: it widens with age at the
          // observed type's own rate and fades as it goes (src/cop/spread.ts, shared with the replay).
          errorM: contactSpreadM(c.typeConf, ageMin, c.errorM),
          ringAlpha: contactRingAlpha(ageMin),
          roleRingPx: role ? roleRingRadiusPx(role.estimate.confidence) : undefined,
          label: ghost ? `last seen ${clockHM(c.lastSeen)}` : contactLabel(c),
        };
      });
      overlay.setContacts(contacts);
      lastSnapshot = snap;
    };
    draw();
    const unsubscribeFeed = useFeed.subscribe((state, previous) => {
      if (state.enrichments === previous.enrichments) return;
      readRoles();
      draw();
    });

    // ---- targeting -------------------------------------------------------------------------------
    let targetingCard: string | null = null;
    const applyTargeting = (s = useStore.getState()): void => {
      const next = s.targeting?.card ?? null;
      if (next === targetingCard) return;
      targetingCard = next;
      container.classList.toggle('targeting-cursor', next !== null);
      if (next === null) overlay.setPreview(null);
    };
    applyTargeting();

    const unsubscribe = useStore.subscribe((s, prev) => {
      // `dials` (specifically C2) changes which position - reported or true - blue units draw at.
      if (s.snapshot !== prev.snapshot || s.selectedUnit !== prev.selectedUnit || s.dials !== prev.dials) draw(s);
      if (s.targeting !== prev.targeting) applyTargeting(s);
      if (s.scenario !== prev.scenario && s.scenario) overlay.setGraphics(s.scenario.graphics);
    });

    /** The order in flight draws a line from the unit to the pointer until the player commits. */
    const onPointerMove = (e: PointerEvent): void => {
      const s = useStore.getState();
      if (!s.targeting || s.selectedUnit === null || !lastSnapshot) return;
      if (card(s.targeting.card).targetKind !== 'point') return;
      const unit = lastSnapshot.units.find((u) => u.id === s.selectedUnit);
      if (!unit) return;
      const rect = container.getBoundingClientRect();
      const ll = map.unproject([e.clientX - rect.left, e.clientY - rect.top]);
      const to = projection.lngLatToCm(ll.lng, ll.lat);
      overlay.setPreview({ id: 'preview', kind: 'axis', name: '', pts: [{ x: unit.x, y: unit.y }, to] });
    };
    container.addEventListener('pointermove', onPointerMove);

    overlay.onClickUnit((id) => {
      const s = useStore.getState();
      const kind = s.targeting ? card(s.targeting.card).targetKind : null;
      if (s.targeting && s.selectedUnit !== null) {
        if (kind === 'unit') {
          s.issue(s.selectedUnit, s.targeting.card, { kind: 'unit', unitId: id });
          return;
        }
        // Units crowd the assembly area, so a destination click that happens to land on one is still a
        // destination: commit the point rather than silently swapping the selection.
        if (kind === 'point') {
          const target = lastSnapshot?.units.find((u) => u.id === id);
          const cell = target && s.grid ? s.grid.cellOfCm(target.x, target.y) : undefined;
          if (cell !== undefined) {
            s.issue(s.selectedUnit, s.targeting.card, { kind: 'point', cell });
            return;
          }
        }
      }
      s.select(id);
    });

    overlay.onClickMap((cm) => {
      const s = useStore.getState();
      if (!s.targeting) {
        s.select(null);
        return;
      }
      const def = card(s.targeting.card);
      const unitId = s.selectedUnit;
      if (unitId === null) return;
      const p = toScreen(cm);
      switch (def.targetKind) {
        case 'point': {
          const cell = s.grid?.cellOfCm(cm.x, cm.y);
          if (cell === undefined) return;
          s.issue(unitId, def.id, { kind: 'point', cell });
          break;
        }
        case 'graphic': {
          let best: { g: Graphic; d: number } | null = null;
          for (const g of s.scenario?.graphics ?? []) {
            const d = distToGraphic(g, p.x, p.y);
            if (d <= HIT_PX && (!best || d < best.d)) best = { g, d };
          }
          if (!best) {
            useStore.setState({ targetingError: 'No control graphic there — click a line, objective or named area' });
            return;
          }
          s.issue(unitId, def.id, { kind: 'graphic', graphicId: best.g.id });
          break;
        }
        case 'contact': {
          let best: { id: number; d: number } | null = null;
          for (const c of lastSnapshot?.picture.contacts ?? []) {
            const q = toScreen({ x: c.x, y: c.y });
            const d = Math.hypot(q.x - p.x, q.y - p.y);
            if (d <= HIT_PX && (!best || d < best.d)) best = { id: c.enemyId, d };
          }
          if (!best) {
            useStore.setState({ targetingError: 'No contact there — click a reported enemy' });
            return;
          }
          s.issue(unitId, def.id, { kind: 'contact', unitId: best.id });
          break;
        }
        case 'unit':
          useStore.setState({ targetingError: 'Click one of your own units' });
          break;
        default:
          break;
      }
    });

    // ---- report feed -> map ping -----------------------------------------------------------------
    setPingHandler((cell) => {
      const s = useStore.getState();
      if (s.targeting) return; // never fight the targeting preview for the same layer
      if (pingTimer.current) clearTimeout(pingTimer.current);
      if (cell === null || !s.grid) {
        overlay.setPreview(null);
        return;
      }
      const c = s.grid.centerCm(cell);
      overlay.setPreview({ id: 'ping', kind: 'nai', name: '', pts: [c], radiusM: 300 });
      pingTimer.current = setTimeout(() => overlay.setPreview(null), PING_MS);
    });

    // ---- direct-manipulation orders (spec Addendum A) ---------------------------------------------
    // Drag a unit to move it; right-press a unit for the radial menu. A card the radial picks that needs
    // a target enters the same targeting mode the deck uses, so everything above still applies.
    const detachGestures = attachMapGestures({
      map, container, projection, overlay, radial: radialController,
      insets: { left: LEFT_RAIL, right: RIGHT_RAIL, top: 48, bottom: 56 },
    });

    // Handles for the end-to-end test and for manual poking in the console. The advisor and feed stores
    // are here so a run can be inspected for what the model actually said - the play, its citations and
    // whether any were dropped, the S-2 assessment - without reading it back out of the DOM.
    //
    // Development and `?debug` only (src/debug.ts): a deployed build does not hand every visitor a
    // console API onto the simulation. `truth()` is the debug-only replacement for the red positions
    // the snapshot used to carry.
    if (DEBUG) {
      (window as unknown as Record<string, unknown>).__wt = {
        overlay, store: useStore, map, projection, advisors: useAdvisors, feed: useFeed,
        truth: () => useStore.getState().sim?.truthUnits() ?? Promise.resolve([]),
      };
    }

    return () => {
      clearTimeout(advisorRelease);
      detachGestures();
      useAdvisors.getState().setMapReady(false);
      unsubscribe();
      unsubscribeFeed();
      setPingHandler(null);
      if (pingTimer.current) clearTimeout(pingTimer.current);
      container.removeEventListener('pointermove', onPointerMove);
      container.classList.remove('targeting-cursor');
      overlay.destroy();
      overlayRef.current = null;
      if (DEBUG) delete (window as unknown as Record<string, unknown>).__wt;
    };
  }, []);

  // Escape always drops out of targeting, wherever the focus is.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') useStore.getState().stopTargeting();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!meta) {
    return (
      <div className="flex h-full items-center justify-center">
        <span className="mono text-xs tracking-[0.3em] text-fog">NO AREA OF OPERATIONS LOADED</span>
      </div>
    );
  }

  return (
    <div
      data-testid="live-stage"
      className="relative h-full w-full overflow-hidden bg-ink"
      // The map's required attribution sits bottom-right by default, which here is under the advisor
      // rail; these two custom properties (read by `.maplibregl-ctrl-bottom-right` in styles.css) move
      // it to the free corner of the map, clear of the rails and the command net meter.
      style={{ '--wt-attrib-bottom': '60px', '--wt-attrib-right': `${RIGHT_RAIL + 4}px` } as CSSProperties}
    >
      {/* The rails float over the map, so the AO is fitted into the gap between them. */}
      <MapView
        meta={meta}
        onReady={handleReady}
        fitPadding={{ left: LEFT_RAIL + 24, right: RIGHT_RAIL + 24, top: 72, bottom: 72 }}
      />

      <RadialMenu />

      <TopBar />
      <TargetingBanner />
        <ElicitCard />
      <RejectionToast overlayRef={overlayRef} />

      <aside
        className="absolute left-0 top-12 bottom-14 z-10 border-r border-slate-700 bg-slate-900/85 backdrop-blur-sm"
        style={{ width: LEFT_RAIL }}
      >
        <ReportFeed />
      </aside>

      {/*
        Unit card, gesture hint and deck take their natural height (shrink-0); advisors is the only
        flexible member (flex: 1 1 auto, min-height: 0) and scrolls its own overflow internally, so
        three long advisor cards never squeeze the order deck's card list to a sliver — which is what
        used to happen when the deck was flexible too and lost the argument every time.

        The gaps and padding are 6 px rather than 8: with the deck's list now holding a hard floor,
        those six pixels are the difference between the third advisor's EXECUTE button sitting inside
        an 800 px viewport and hanging just below it.
      */}
      <aside
        className="absolute right-0 top-12 bottom-14 z-10 flex flex-col gap-1.5 overflow-hidden border-l border-slate-700 bg-slate-900/85 p-1.5 backdrop-blur-sm"
        style={{ width: RIGHT_RAIL }}
      >
        <SelectedUnitPanel />
        <Advisors context="live" />
        <p data-testid="gesture-hint" className="mono shrink-0 px-1 text-[10px] leading-tight text-fog">
          Drag a unit to move it. Right-click a unit for other orders — fires,{' '}
          <Acronym k="isr">ISR</Acronym>, sustainment. The deck below is the same hand, with its costs.
        </p>
        <Deck />
      </aside>

      <BottomBar />
      <EndBanner />
      <CoachOverlay />
    </div>
  );
}

// ---- top bar -------------------------------------------------------------------------------------

/**
 * The speed chips, as real sim:real-time ratios rather than the old, illegible "1x"/"2x"/"4x" (2026-09-21
 * review, item A.4: "1x is 60:1 wall clock ... imperceptible"). `ratio` is sim-seconds per real second,
 * which is also sim-minutes per real minute - the tooltip's unit.
 */
const SPEEDS: { value: Speed; label: string; testId: string; ratio: number }[] = [
  { value: SPEED_1_1, label: '1:1', testId: 'speed-1to1', ratio: 1 },
  { value: SPEED_10_1, label: '10:1', testId: 'speed-10to1', ratio: 10 },
  { value: SPEED_60_1, label: '60:1', testId: 'speed-60to1', ratio: 60 },
  { value: SPEED_240_1, label: '240:1', testId: 'speed-240to1', ratio: 240 },
];

function TopBar() {
  const tick = useStore((s) => s.snapshot?.tick ?? 0);
  const phase = useStore((s) => s.snapshot?.phase ?? '—');
  const speed = useStore((s) => s.speed);
  const dials = useStore((s) => s.dials);
  const setSpeed = useStore((s) => s.setSpeed);
  const scenarioName = useStore((s) => s.scenario?.names.tf ?? '');

  return (
    <header className="absolute inset-x-0 top-0 z-20 flex h-12 items-center gap-3 border-b border-slate-700 bg-slate-900/90 px-3 backdrop-blur-sm">
      <Wordmark />
      <span data-testid="clock" className="mono text-[18px] tracking-[0.1em] text-paper">{clockHM(tick)}</span>
      <div className="flex items-center gap-1">
        {/* The "live pressure" dial takes the pause chip away; the rest of the clock stays the player's. */}
        {!dials.pressure && (
          <Chip active={speed === 0} onClick={() => setSpeed(0)} testId="speed-0" title="Pause the clock">pause</Chip>
        )}
        {SPEEDS.map((s) => (
          <Chip
            key={s.value}
            active={speed === s.value}
            onClick={() => setSpeed(s.value)}
            testId={s.testId}
            title={`${s.ratio} sim minute${s.ratio === 1 ? '' : 's'} per real minute`}
          >
            {s.label}
          </Chip>
        ))}
      </div>
      <AutoSlowNotice />
      {/* The fitted scenario already names the force "TF VANGUARD"; `AcronymText` marks the TF in it. */}
      <span className="mono truncate text-[11px] text-fog">
        <AcronymText>{scenarioName}</AcronymText>
      </span>
      <span data-testid="phase" className="mono truncate text-[11px] text-paper">{phase}</span>
      <WindTunnelChip />
      <div className="ml-auto flex items-center gap-1.5">
        <DialReadout k="c2" value={dials.c2} />
        <DialReadout k="ew" value={dials.ew} />
        <DialReadout k="tod" value={dials.tod} />
        <DialReadout k="red" value={dials.red} label="red" title="Enemy posture: where the enemy has chosen to fight you" />
        <span
          data-testid="dial-readout-rules"
          className="mono text-[10px] text-fog"
          title="The active ruleset: every tuning constant the sim runs on (data/rules.json), set from the Instructor panel"
        >
          {rulesetLabel(dials.rules.preset, dials.rules.overrides)}
        </span>
        {dials.pressure && <Chip tone="warn" active title="Live pressure: the pause control is removed">live pressure</Chip>}
      </div>
    </header>
  );
}

// ---- refused orders ------------------------------------------------------------------------------

/**
 * An order the simulation refused, said where the player is looking.
 *
 * Most refusals never get this far: `store.issue` runs the same legality check the sim runs and puts the
 * reason on the targeting banner (or, for a dragged move, in the gesture controller's own toast) before
 * anything is sent. But some rules can only be judged inside the simulation against the picture at that
 * tick - a fire mission needs an observer holding a contact within 300 m of the target (pitch 9c,
 * `src/sim/orders.ts`), and the deck cannot know that from the card and the cursor alone. Those orders
 * look accepted: the banner closes, the crosshair goes, and the only trace used to be one low-ranked
 * card in a feed of a hundred and sixty.
 *
 * So the refusal is said twice, as the same refusal: here, over the unit that was refused, for six
 * seconds; and in the report feed as a card ranked `high` (`src/ui/feed/triage.ts`), which is where the
 * player looks for it a minute later.
 */
function RejectionToast({ overlayRef }: { overlayRef: RefObject<Overlay | null> }) {
  const events = useStore((s) => s.events);
  const [toast, setToast] = useState<{ id: number; text: string; reason: string; x: number; y: number } | null>(null);
  /** The newest refusal already shown, so the same one is never re-raised as the feed grows. */
  const shownId = useRef(-1);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let latest: Event | null = null;
    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i];
      if (e.kind === 'order.rejected' && e.side === 0) { latest = e; break; }
    }
    if (!latest || latest.id === shownId.current) return;
    shownId.current = latest.id;

    const snap = useStore.getState().snapshot;
    const unit = snap?.units.find((u) => u.id === latest!.unitId);
    // Over the unit if it is on screen; otherwise centred in the map's own gap between the two rails,
    // which is the one place on this stage nothing else occupies.
    const at = latest.unitId === undefined ? null : overlayRef.current?.screenPosOf(latest.unitId) ?? null;
    const reason = typeof latest.payload.reason === 'string' ? latest.payload.reason : 'not a legal order';
    setToast({
      id: latest.id,
      reason,
      text: `${unit?.callsign ?? 'TF'} — ${cardLabel(latest.payload.card)} refused`,
      x: at ? at.x : (LEFT_RAIL + window.innerWidth - RIGHT_RAIL) / 2,
      y: at ? at.y : 200,
    });
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(null), REJECT_TOAST_MS);
  }, [events, overlayRef]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  if (!toast) return null;
  return (
    <div
      data-testid="order-rejected-toast"
      data-reason={toast.reason}
      className="mono fade-in pointer-events-none absolute z-30 whitespace-nowrap rounded-sm px-2 py-1 text-[11px] leading-tight"
      style={{
        left: Math.round(Math.min(Math.max(toast.x, LEFT_RAIL + 90), window.innerWidth - RIGHT_RAIL - 90)),
        top: Math.round(Math.max(toast.y - 34, 56)),
        transform: 'translate(-50%, -100%)',
        border: '1px solid var(--color-hostile)',
        background: 'color-mix(in srgb, var(--color-slate-900) 95%, transparent)',
        color: 'var(--color-hostile)',
        letterSpacing: '0.06em',
      }}
    >
      {toast.text}
      <span className="block text-paper">{toast.reason}</span>
    </div>
  );
}

// ---- the clock -----------------------------------------------------------------------------------

/**
 * Pitch 2's auto-slow notice: the clock just dropped to 10:1 on its own because a significant event
 * landed while the player was watching at 60:1 or 240:1 - too fast to feel the C2 (command and control)
 * delay or the net queue land.
 *
 * It puts itself back. The notice counts the seconds down ("resuming 240:1 in 12 s") so the clock never
 * changes under the player without having said it would. If the player does anything inside that window
 * - issues an order, picks a unit, touches a speed chip - the countdown stops and the clock stays at
 * 10:1: someone acting on the event is not waiting to be hurried. The resume chip is then theirs, and
 * it is the undo rather than a dismiss: it puts the clock back where they had it.
 */
function AutoSlowNotice() {
  const autoSlow = useStore((s) => s.autoSlow);
  const resumeSpeed = useStore((s) => s.resumeSpeed);
  // One ticker while a countdown is on screen. 250 ms rather than 1000 so the displayed second is never
  // more than a quarter of a second stale against the restore the store will actually perform.
  const [now, setNow] = useState(() => Date.now());
  const counting = autoSlow !== null && !autoSlow.held;
  useEffect(() => {
    if (!counting) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [counting, autoSlow?.restoreAt]);

  if (!autoSlow) return null;
  const back = SPEEDS.find((s) => s.value === autoSlow.previousSpeed)?.label ?? 'the previous speed';
  const left = autoSlowSecondsLeft(autoSlow, now);
  return (
    <div data-testid="auto-slow-notice" data-held={autoSlow.held ? 'true' : 'false'} className="fade-in flex items-center gap-1.5">
      <span className="mono text-[10px] tracking-[0.05em]" style={{ color: 'var(--color-warn)' }}>
        Slowed to 10:1 — significant event
        {left === null
          ? <span data-testid="auto-slow-held"> · held while you work</span>
          : <span data-testid="auto-slow-countdown">{` · resuming ${back} in ${left} s`}</span>}
      </span>
      <Chip
        tone="warn"
        onClick={resumeSpeed}
        testId="auto-slow-resume"
        title={`Resume ${back} now, the speed the clock was at before this event`}
      >
        resume
      </Chip>
    </div>
  );
}

/**
 * One dial, as the top bar shows it: the dial's letters quiet and hoverable, its setting loud.
 *
 * These were four 10 px chips with the name and the value in one undifferentiated run of small caps -
 * legible on the designer's monitor, unreadable across a room. The name is now an `<Acronym>`, so
 * "C2" says what it stands for and what it costs you on hover, and the value carries the weight.
 */
function DialReadout({
  k, value, label, title,
}: { k: string; value: string; label?: string; title?: string }) {
  return (
    <span className="wt-dial" data-testid={`dial-readout-${k}`} title={title}>
      <span className="k">{label ? label : <Acronym k={k} />}</span>
      <span className="v">{value}</span>
    </span>
  );
}

// ---- targeting banner ----------------------------------------------------------------------------

/** What counts as a target for each kind, appended to the banner's standing instruction. */
const TARGET_PROMPT: Record<string, string> = {
  point: '',
  graphic: 'the target is a control graphic — a line, an objective or a named area',
  unit: 'the target is one of your own units',
  contact: 'the target is a reported enemy contact',
  none: '',
  text: '',
};

/** One instruction, whatever put the stage into targeting: the deck, or a wedge of the radial menu. */
const TARGET_INSTRUCTION = 'MOVE THE CURSOR TO THE TARGET, CLICK TO COMMIT — Esc cancels';

function TargetingBanner() {
  const targeting = useStore((s) => s.targeting);
  const error = useStore((s) => s.targetingError);
  const stop = useStore((s) => s.stopTargeting);
  if (!targeting && !error) return null;
  const def = targeting ? card(targeting.card) : null;
  return (
    <div
      data-testid="targeting-banner"
      // The banner floats over the map while the player is hunting for a target, so it must not eat the
      // click that commits the order; only the cancel chip takes pointer events.
      className="fade-in pointer-events-none absolute left-1/2 top-16 z-20 -translate-x-1/2 rounded-sm border bg-slate-900/95 px-3 py-1.5"
      style={{ borderColor: error ? 'var(--color-hostile)' : 'var(--color-friendly)' }}
    >
      <div className="flex items-center gap-3">
        <span className="mono text-[11px] tracking-[0.1em]" style={{ color: error ? 'var(--color-hostile)' : 'var(--color-friendly)' }}>
          {error ? error : `${def!.label.toUpperCase()} — ${TARGET_INSTRUCTION}`}
        </span>
        <span className="pointer-events-auto">
          <Chip onClick={stop} testId="targeting-cancel" title="Escape also cancels">cancel</Chip>
        </span>
      </div>
      {!error && TARGET_PROMPT[def!.targetKind] !== '' && (
        <div className="mono mt-0.5 text-[10px] text-fog">{TARGET_PROMPT[def!.targetKind]}</div>
      )}
    </div>
  );
}

// ---- right rail ----------------------------------------------------------------------------------

function SelectedUnitPanel() {
  const snapshot = useStore((s) => s.snapshot);
  const selectedUnit = useStore((s) => s.selectedUnit);
  const unit = useMemo(
    () => (snapshot && selectedUnit !== null ? snapshot.units.find((u) => u.id === selectedUnit) ?? null : null),
    [snapshot, selectedUnit],
  );
  if (!unit || !snapshot) {
    return (
      <section className="wt-empty shrink-0" data-testid="no-unit-selected">
        No unit selected. Click a symbol on the map — or drag one straight to where it should go.
      </section>
    );
  }
  return <UnitCard unit={unit} snapshot={snapshot} />;
}

// ---- bottom bar ----------------------------------------------------------------------------------

function BottomBar() {
  const force = useStore((s) => s.snapshot?.force);
  const net = useStore((s) => s.snapshot?.net);
  const orders = useStore((s) => s.snapshot?.orders);
  const tick = useStore((s) => s.snapshot?.tick ?? 0);
  return (
    <footer className="absolute inset-x-0 bottom-0 z-20 flex h-14 items-center gap-6 border-t border-slate-700 bg-slate-900/90 px-3 backdrop-blur-sm">
      <NetMeter occupancy={net?.occupancy ?? 0} queueSec={net?.queueSec ?? 0} orders={orders ?? []} tick={tick} />
      <div className="w-[420px] shrink-0 space-y-1">
        <Bar label="str" value={force?.strength ?? 0} color="var(--color-friendly)" title="Task force average strength" />
        <Bar label="fue" value={force?.fuel ?? 0} color="var(--color-isr)" title="Task force average fuel" />
        <Bar label="amm" value={force?.ammo ?? 0} color="var(--color-command)" title="Task force average ammunition" />
      </div>
    </footer>
  );
}

const END_TEXT: Record<NonNullable<Snapshot['ended']>, string> = {
  seized: 'Objective seized.',
  culminated: 'The task force culminated — it can no longer attack.',
  time: 'Time limit reached.',
};

function EndBanner() {
  const ended = useStore((s) => s.snapshot?.ended ?? null);
  if (!ended) return null;
  return (
    <div className="fade-in absolute left-1/2 top-1/2 z-30 -translate-x-1/2 -translate-y-1/2 rounded-sm border border-slate-700 bg-slate-900/95 px-6 py-4 text-center">
      <div className="mono text-[10px] uppercase tracking-[0.3em] text-fog">End of run</div>
      <div className="mt-1 text-[15px] text-paper">{END_TEXT[ended]}</div>
      <div className="mono mt-1 text-[11px] text-fog">Building the debrief…</div>
    </div>
  );
}
